import type { UploadedResource } from "../media/uploadedResources"

export const MAX_VIDEO_GENERATION_REQUEST_BODY_SIZE = 60 * 1024 * 1024

export type VideoGenerationResource = Pick<UploadedResource, "size" | "url">

export class VideoGenerationRequestValidationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "VideoGenerationRequestValidationError"
  }
}

const getVideoGenerationResourceUrls = (params: unknown) => {
  if (typeof params !== "object" || params === null) return []

  const {
    audio_urls: audioUrls,
    image_urls: imageUrls,
    video_urls: videoUrls,
  } = params as {
    audio_urls?: unknown
    image_urls?: unknown
    video_urls?: unknown
  }

  return [imageUrls, audioUrls, videoUrls].flatMap((urls) => {
    return Array.isArray(urls)
      ? urls.filter((url): url is string => typeof url === "string" && !!url)
      : []
  })
}

const getRemoteResourceSize = async (url: string) => {
  if (url.startsWith("data:")) return 0

  let response: Response

  try {
    response = await fetch(url, { method: "HEAD" })
  } catch {
    throw new Error("获取资源大小失败")
  }

  const contentLength = response.headers.get("content-length")
  const size = contentLength ? Number(contentLength) : Number.NaN

  if (!response.ok || !Number.isFinite(size) || size < 0) {
    throw new Error("获取资源大小失败")
  }

  return size
}

export const getResourceSize = async (
  resourceUrls: string[],
  resources: VideoGenerationResource[] = [],
) => {
  const knownResourceSizeMap = new Map(
    resources.flatMap((resource) => {
      return typeof resource.size === "number" &&
        Number.isFinite(resource.size) &&
        resource.size >= 0
        ? [[resource.url, resource.size] as const]
        : []
    }),
  )
  const resourceSizes = await Promise.all(
    resourceUrls.map((url) => {
      return knownResourceSizeMap.has(url)
        ? Promise.resolve(knownResourceSizeMap.get(url) || 0)
        : getRemoteResourceSize(url)
    }),
  )

  return resourceSizes.reduce((total, size) => total + size, 0)
}

export const getVideoGenerationRequestBodySizeError = async (
  params: unknown,
  resources: VideoGenerationResource[] = [],
) => {
  const body = JSON.stringify(params)
  const bodySize = new TextEncoder().encode(body).byteLength
  const requestResourceUrls = getVideoGenerationResourceUrls(params)
  const resourceUrls = requestResourceUrls.length
    ? requestResourceUrls
    : resources.map((resource) => resource.url)

  let resourceSize: number

  try {
    resourceSize = await getResourceSize(resourceUrls, resources)
  } catch {
    return "无法获取生成视频所需资源的大小，请重新上传后再试"
  }

  return bodySize + resourceSize > MAX_VIDEO_GENERATION_REQUEST_BODY_SIZE
    ? "当前上传图片、音频等素材总大小超出接口60M上限，请删减或压缩素材后重新提交"
    : ""
}

export const validateVideoGenerationRequestBodySize = async (
  params: unknown,
  resources?: VideoGenerationResource[],
) => {
  const errorMessage = await getVideoGenerationRequestBodySizeError(
    params,
    resources,
  )

  if (errorMessage) {
    throw new VideoGenerationRequestValidationError(errorMessage)
  }
}
